<template>
  <section class="mt-4 space-y-3" aria-label="Propositions de remplacement">
    <p v-if="loadInfo" class="text-sm text-green-800" role="status">{{ loadInfo }}</p>
    <p v-if="loadError" class="text-sm text-red-700" role="alert">{{ loadError }} <button type="button" class="underline" @click="load">Réessayer</button></p>
    <article v-for="proposal in proposals" :key="proposal.id_jet" class="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex flex-wrap items-center gap-2">
          <span role="status" class="inline-flex items-center rounded-full px-3 py-1.5 text-sm font-semibold ring-1 ring-inset" :class="replacementStatusClasses[displayStatus(proposal)]">{{ replacementStatus[displayStatus(proposal)] }}</span>
          <span class="text-xs text-gray-500">v{{ proposal.version }}</span>
        </div>
        <div v-if="proposal.statut === 'pending' && remainingSeconds(proposal) > 0" class="flex flex-wrap gap-3">
          <button v-if="canEditRecommendations && remainingSeconds(proposal) > 0" type="button" class="text-indigo-700 underline" @click="expandedProposalId = expandedProposalId === proposal.id_jet ? null : proposal.id_jet">Modifier</button>
          <button type="button" :disabled="busy" class="text-red-700 underline disabled:opacity-50" @click="cancel(proposal.id_jet)">Annuler</button>
        </div>
      </div>
      <template v-if="proposal.statut === 'pending' && remainingSeconds(proposal) > 0">
        <p class="mt-3 inline-flex items-center rounded-full bg-amber-100 px-3 py-1.5 font-semibold tabular-nums text-amber-900 ring-1 ring-inset ring-amber-200" :title="`Réponse avant le ${date(responseDeadline(proposal))}`" role="timer" aria-label="Temps restant pour répondre">Temps restant : {{ countdown(proposal) }}</p>
        <p class="mt-2 text-gray-600">Sans réponse, les articles restent en rupture.</p>
      </template>
      <p v-if="hasExpiredLines(proposal)" class="mt-2 text-amber-800" role="status">Les articles sans réponse restent en rupture.</p>
      <details :open="expandedProposalId === proposal.id_jet" class="mt-2" @toggle="onDetailsToggle(proposal.id_jet, $event)"><summary class="cursor-pointer">Articles et montants</summary><ReplacementComparison :lines="proposal.lignes" :provisional="proposal.montants_provisoires" :editable="canEditRecommendations && proposal.statut === 'pending' && remainingSeconds(proposal) > 0" class="mt-3" @edit="(line, product, action) => openCandidateEditor(proposal, line, product, action)" /></details>
    </article>
    <EditReplacementCandidateModal v-if="canEditRecommendations" ref="candidateEditor" @saved="onCandidateSaved" />
    <ModalComp v-model:isVisible="visible" title="Proposer des remplacements" size="xl" class="replacement-proposal-modal">
      <template #header>
        <div>
          <h3 class="text-lg font-semibold tracking-tight text-gray-900">Proposer des remplacements</h3>
          <p class="mt-1 text-xs text-gray-500">Des alternatives pour vos articles en rupture.</p>
        </div>
      </template>
      <template #body>
        <ol ref="stepsHeader" tabindex="-1" class="mb-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-gray-100 pb-4 text-sm" aria-label="Étapes de la proposition">
          <li class="flex items-center gap-2" :aria-current="step === 1 ? 'step' : undefined" :class="step === 1 ? 'font-semibold text-indigo-700' : 'text-gray-500'"><span class="flex h-6 w-6 items-center justify-center rounded-full text-xs" :class="step === 1 ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'" aria-hidden="true">1</span>Choisir les produits</li>
          <li class="flex items-center gap-2" :aria-current="step === 2 ? 'step' : undefined" :class="step === 2 ? 'font-semibold text-indigo-700' : 'text-gray-500'"><span class="flex h-6 w-6 items-center justify-center rounded-full text-xs" :class="step === 2 ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'" aria-hidden="true">2</span>Quantités et confirmation</li>
        </ol>
        <div class="grid items-start gap-5 md:grid-cols-[14rem_minmax(0,1fr)]">
        <fieldset class="replacement-sidebar min-w-0 rounded-xl border border-gray-100 bg-gray-50 p-3 md:sticky md:top-0">
          <legend class="px-1 text-sm font-semibold text-gray-900">Articles à remplacer <span class="font-normal text-gray-500">({{ activeOriginIds.length }})</span></legend>
          <p class="mb-3 text-xs text-gray-500">Sélectionnez un article.</p>
          <div class="grid gap-2">
            <div v-for="id in activeOriginIds" :key="id" class="relative min-w-0 rounded-xl border text-sm transition-colors" :class="step === 2 ? [originConfirmed(id) ? 'replacement-article--confirmed' : 'replacement-article--pending', { 'replacement-article--active': searchTargetLineId === id }] : searchTargetLineId === id ? 'border-indigo-400 bg-white ring-1 ring-indigo-400' : 'border-gray-200 bg-white hover:border-gray-300'">
              <button type="button" :disabled="busy" :aria-pressed="searchTargetLineId === id" :aria-label="`Choisir les remplacements pour ${original(id)?.reference || id}`" class="flex w-full min-w-0 items-center gap-2 rounded-lg py-2 pl-2 pr-8 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 disabled:opacity-50" @click="selectOrigin(id)">
                <ReplacementProductPhoto :src="original(id)?.imageUrl" :reference="original(id)?.reference" class="h-16 w-12" />
                <span class="min-w-0 flex-1"><strong class="break-words text-xs font-semibold leading-5 text-gray-900">{{ original(id)?.reference || id }}</strong><span class="mt-1 block text-xs" :class="step === 2 ? originConfirmed(id) ? 'text-green-700' : 'text-amber-800' : selectedFor(id).length ? 'text-indigo-600' : 'text-gray-500'">{{ selectedFor(id).length }} candidat(s)<template v-if="step === 2"> · {{ originConfirmed(id) ? 'Confirmé' : 'À confirmer' }}</template></span></span>
              </button>
              <button type="button" :disabled="busy" :aria-label="`Retirer ${original(id)?.reference || id} de cette proposition`" title="Retirer de cette proposition" class="absolute right-0 top-0 flex h-8 w-8 items-center justify-center rounded text-xl text-gray-600 hover:bg-red-100 hover:text-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 disabled:opacity-50" @click="toggleOrigin(id)"><span aria-hidden="true">×</span></button>
            </div>
          </div>
          <p class="mt-3 text-xs leading-5 text-gray-500">Un article retiré reste en rupture.</p>
          <div v-if="removedOriginIds.length" class="mt-3 space-y-2 border-t pt-3 text-sm">
            <p class="text-xs text-gray-500">Articles retirés</p>
            <button v-for="id in removedOriginIds" :key="id" type="button" :disabled="busy" class="block w-full break-words rounded border px-2 py-1 text-left text-indigo-700 disabled:opacity-50" @click="toggleOrigin(id)">+ Réajouter {{ original(id)?.reference || id }}</button>
          </div>
        </fieldset>
        <div v-if="step === 1" class="min-w-0">
        <form v-if="activeOriginIds.length" class="replacement-search mb-4 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2" @submit.prevent="searchProducts">
          <ReplacementReferenceSearch v-model="search" :products="referenceSuggestions" :selection="searchTarget" :loading="searching" :disabled="busy" @choose="chooseReference" @search="searchProducts" />
          <button type="submit" :disabled="searching || busy" class="col-start-2 row-start-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50">{{ searching ? 'Recherche…' : 'Rechercher' }}</button>
        </form>
        <p v-if="activeOriginIds.length && appliedSearch" class="mb-4 break-words text-xs text-gray-600">Recherche : « {{ appliedSearch }} »</p>
        <div v-for="originId in visibleOriginIds" :id="`replacement-origin-${originId}`" :key="originId" tabindex="-1" class="space-y-4">
          <div class="replacement-origin-summary relative flex flex-wrap items-start justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3 pr-10">
            <button type="button" :disabled="busy" :aria-label="`Retirer ${original(originId)?.reference || originId} de cette proposition`" title="Retirer de cette proposition" class="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded text-xl text-gray-600 hover:bg-red-100 hover:text-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 disabled:opacity-50" @click="toggleOrigin(originId)"><span aria-hidden="true">×</span></button>
            <div class="flex w-full min-w-0 items-start gap-3 text-sm sm:w-auto sm:flex-1">
              <ReplacementProductPhoto :src="original(originId)?.imageUrl" :reference="original(originId)?.reference" class="h-16 w-12" />
              <div class="min-w-0">
                <p class="mb-1 text-xs font-medium text-red-600">En rupture</p>
                <p class="break-words text-sm font-semibold text-gray-900">{{ original(originId)?.reference || originId }}</p>
                <p class="mt-1 text-xs text-gray-500">{{ original(originId)?.categorie || 'Catégorie non renseignée' }}<template v-if="originalQuantity(originId)"> · {{ originalQuantity(originId) }} pièce(s)</template></p>
              </div>
            </div>
            <div v-if="originalAmount(originId)" class="text-xs leading-5">
              <p class="text-gray-500">Montant d’origine · avant remises</p>
              <p class="font-medium text-gray-900">{{ originalAmount(originId) }}</p>
            </div>
            <p v-else class="text-xs text-gray-600">Montant d’origine non renseigné.</p>
          </div>
          <ReplacementProductPicker
            class="replacement-picker"
            v-if="originEnabled(originId)"
            :products="availableProducts(originId)"
            :selected-ids="selectedFor(originId).map(s => s.id_produit_remplacement)"
            :selections="selectedFor(originId)"
            :loading="searching"
            :disabled="busy"
            :legend="appliedSearch ? 'Résultats de recherche' : 'Suggestions de remplacement'"
            @choose="toggleProduct(originId, $event)"
          />
          <p v-if="selectedFor(originId).length" class="border-t border-gray-100 pt-4 text-sm font-semibold text-gray-900">Candidats retenus <span class="font-normal text-gray-500">({{ selectedFor(originId).length }}/10) · 1 au choix</span></p>
          <div v-for="selection in selectedFor(originId)" :key="selection.id_produit_remplacement" class="rounded-xl border border-indigo-100 bg-indigo-50 p-3">
            <div class="flex items-start gap-3">
              <ReplacementProductPhoto :src="product(selection)?.image_url" :reference="product(selection)?.reference" class="h-14 w-12" />
              <div class="min-w-0 flex-1 space-y-1 text-sm">
                <p class="break-words text-xs font-semibold text-gray-900">{{ product(selection)?.reference || 'Candidat indisponible' }}</p>
                <p class="text-xs text-gray-500">{{ product(selection)?.couleur }} · {{ selectedProductTotal(selection).description }}</p>
                <p class="text-xs font-medium text-indigo-700">{{ selectedProductTotal(selection).amount != null ? `${replacementMoney(selectedProductTotal(selection).amount ?? 0)} HT au total` : 'Quantité à préciser' }}</p>
              </div>
              <button type="button" :disabled="busy" class="shrink-0 rounded-md px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-white hover:text-red-700 disabled:opacity-50" @click="removeProduct(selection)">Retirer</button>
            </div>
          </div>
        </div>
        <p v-if="!activeOriginIds.length" class="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">Réajoutez un article pour choisir ses remplacements.</p>
        </div>
        <div v-else class="min-w-0 space-y-4" aria-label="Quantités et montants des remplacements">
          <section v-for="originId in visibleOriginIds" :id="`replacement-origin-${originId}`" :key="originId" tabindex="-1" class="space-y-4" :aria-label="`Remplacements pour ${original(originId)?.reference || originId}`">
            <div class="space-y-2">
              <p class="text-sm font-semibold text-indigo-700">Remplacements proposés<template v-if="selectedFor(originId).length > 1"><span class="font-normal text-gray-500"> · 1 au choix</span></template></p>
              <p class="text-xs text-gray-500">Renseignez les quantités de chaque candidat ci-dessous.</p>
            </div>
            <article class="overflow-hidden rounded-xl border border-gray-200">
              <div>
                <details open class="min-w-0 space-y-3 border-b border-gray-100 bg-gray-50 p-4 text-sm">
                  <summary class="cursor-pointer text-xs text-gray-600">Détails de l’article en rupture</summary>
                  <div class="mt-3 flex items-start gap-3">
                    <ReplacementProductPhoto :src="original(originId)?.imageUrl" :reference="original(originId)?.reference" class="h-16 w-12" />
                    <div class="min-w-0 space-y-2 break-words">
                      <p class="font-semibold">{{ original(originId)?.reference || originId }}</p>
                      <p>{{ original(originId)?.color }}</p>
                      <p>{{ originalSizes(originId) }}</p>
                      <p><template v-if="original(originId)?.vendu_par !== 'tailles' && original(originId)?.quantite_pack">{{ original(originId)?.quantite_pack }} paquet(s) · </template>{{ originalQuantity(originId) }} pièce(s)</p>
                      <p v-if="quotedLine(originId)">{{ replacementMoney(quotedLine(originId)?.origine.montant_ht ?? 0) }} HT · {{ replacementMoney(quotedLine(originId)?.origine.montant_ttc ?? 0) }} TTC</p>
                      <p v-else>{{ originalAmount(originId) || 'Montant d’origine non renseigné.' }}</p>
                    </div>
                  </div>
                </details>
                <div class="min-w-0 space-y-4 bg-white p-4 text-sm">
                  <p class="text-xs font-semibold text-indigo-700">Quantités des candidats</p>
                  <div v-for="selection in selectedFor(originId)" :key="selection.id_produit_remplacement" class="space-y-3 border-b border-gray-100 pb-5 last:border-0 last:pb-0">
                    <div class="flex items-start gap-3">
                      <ReplacementProductPhoto :src="product(selection)?.image_url" :reference="product(selection)?.reference" class="h-20 w-16" />
                      <div class="min-w-0 flex-1 space-y-2 break-words">
                        <p class="font-semibold">{{ product(selection)?.reference }}</p>
                        <p>{{ product(selection)?.couleur }}</p>
                      </div>
                      <button type="button" :disabled="busy" class="shrink-0 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-50 hover:text-red-700 disabled:opacity-50" @click="removeProduct(selection)">Retirer</button>
                    </div>
                    <label v-if="product(selection)?.vendu_par !== 'tailles'" class="block">Quantité de paquets
                      <input v-model.number="selection.quantite_pack" :disabled="busy" type="number" min="1" max="10000" class="mt-1 block w-28 rounded-lg border border-gray-200 p-2 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100" @input="updatePacks(selection)" />
                    </label>
                    <p v-if="product(selection)?.vendu_par !== 'tailles'" class="text-xs text-gray-600">0 = taille non souhaitée. Modifier les paquets réinitialise les quantités.</p>
                    <div class="flex flex-wrap gap-3">
                      <label v-for="size in sizeInputs(selection)" :key="size.index">{{ size.label }}
                        <input :value="selection.quantites[size.index]" :disabled="busy" type="number" min="0" :max="product(selection)?.vendu_par === 'tailles' ? 10000 : Math.min(10000, (product(selection)?.pack[size.index] || 0) * selection.quantite_pack)" class="mt-1 block w-20 rounded-lg border border-gray-200 p-2 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100" @input="updateSizeQuantity(selection, size.index, $event)" />
                      </label>
                    </div>
                    <p>{{ selection.quantites.reduce((sum, q) => sum + (Number(q) || 0), 0) }} pièce(s)</p>
                    <template v-if="quotedProduct(selection)">
                      <p>{{ replacementMoney(quotedProduct(selection)?.montant_ht ?? 0) }} HT · {{ replacementMoney(quotedProduct(selection)?.montant_ttc ?? 0) }} TTC</p>
                      <p v-if="quotedProduct(selection)?.charge_vendeur_ttc" class="text-amber-800">Charge vendeur si choisi : {{ replacementMoney(quotedProduct(selection)?.charge_vendeur_ttc ?? 0) }} TTC</p>
                      <p v-else-if="quotedProduct(selection)?.a_rembourser_ttc" class="text-blue-700">Remboursement estimé : {{ replacementMoney(quotedProduct(selection)?.a_rembourser_ttc ?? 0) }} TTC (hors remise fixe)</p>
                    </template>
                    <ul v-else-if="!selectionReady(selection)" class="space-y-1 text-amber-800" role="status">
                      <li v-for="message in quantityErrors(selection)" :key="message">{{ message }}</li>
                    </ul>
                    <p v-else class="text-indigo-700" role="status">{{ quoting ? 'Calcul du devis…' : 'Devis indisponible.' }}</p>
                  </div>
                  <label v-if="selectedFor(originId).length" class="flex items-start gap-3 rounded-xl border border-indigo-100 bg-indigo-50 p-3 text-sm text-indigo-950" :class="busy || quoting || originQuoteSignature(originId) === null ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'">
                    <input :checked="originConfirmed(originId)" :disabled="busy || quoting || originQuoteSignature(originId) === null" type="checkbox" class="mt-0.5 h-4 w-4 shrink-0" @change="confirmOrigin(originId, $event)" />
                    <span>Je confirme les quantités, la disponibilité et les éventuels écarts de prix à ma charge pour tous les candidats de cet article.</span>
                  </label>
                  <template v-if="!selectedFor(originId).length">
                    <p>Aucun candidat proposé.</p>
                    <button type="button" :disabled="busy" class="font-medium text-indigo-700 underline" @click="focusOrigin(originId)">Choisir un produit</button>
                  </template>
                </div>
              </div>
            </article>
          </section>
          <p v-if="!activeOriginIds.length" class="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">Réajoutez un article pour choisir ses remplacements.</p>
        </div>
        </div>
        <p v-if="step === 2 && preview && fixedDiscount" class="mt-3 text-sm text-gray-600">Remise prise en compte : {{ replacementMoney(fixedDiscount) }} HT</p>
      </template>
      <template #footer>
        <div class="w-full space-y-3 border-t border-gray-100 pt-4">
          <p v-if="quoting" class="text-sm text-indigo-700" role="status">Calcul en cours…</p>
          <div v-if="stepIncompleteOrigins.length" class="text-xs text-gray-500" role="status">
            <p>{{ step === 1 ? 'Choisissez un produit pour :' : 'Vérifiez les quantités pour :' }}</p>
            <div class="mt-1 flex flex-wrap gap-x-3 gap-y-2">
              <span v-for="id in stepIncompleteOrigins" :key="id" class="relative inline-flex min-w-0 max-w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-2 pr-7">
                <button type="button" :disabled="busy" class="min-w-0 break-words text-left font-medium text-gray-700 hover:text-indigo-700 disabled:opacity-50" @click="focusOrigin(id)">{{ original(id)?.reference || id }}</button>
                <button type="button" :disabled="busy" :aria-label="`Retirer ${original(id)?.reference || id} de cette proposition`" title="Retirer de cette proposition" class="absolute right-0 top-0 flex h-6 w-6 items-center justify-center rounded text-lg hover:bg-red-100 hover:text-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 disabled:opacity-50" @click="toggleOrigin(id)"><span aria-hidden="true">×</span></button>
              </span>
            </div>
          </div>
          <p v-else-if="!selections.length" class="text-sm text-gray-600" role="status">Ajoutez au moins un article à remplacer.</p>
          <p v-if="modalError || quoteError" class="text-sm text-red-700" role="alert">{{ quoteError || modalError }} <button v-if="quoteError && !quoting" type="button" class="underline" @click="refreshQuote">Réessayer</button></p>
          <p v-if="step === 2 && sellerCharge > 0" class="rounded border border-amber-300 bg-amber-50 p-2 text-sm font-semibold text-amber-900" role="status">Si l’un des produits de remplacement proposés est plus cher que l’article en rupture correspondant, la différence sera à la charge du vendeur.</p>
          <p v-if="step === 2 && selections.length" class="text-xs text-gray-500" role="status">Articles confirmés : {{ confirmedOriginCount }} / {{ activeOriginIds.length }}. Confirmez chaque article avant de proposer.</p>
          <div class="replacement-actions flex flex-wrap justify-end gap-2">
            <button type="button" :disabled="busy" class="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50" @click="visible = false">Fermer</button>
            <button v-if="step === 2" type="button" :disabled="busy" class="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50" @click="previousStep">Retour</button>
            <button v-if="step === 1" type="button" :disabled="!canPropose || busy || searching || !candidatesChosen" class="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40" @click="nextStep">Suivant</button>
            <button v-else type="button" :disabled="!canPropose || busy || quoting || !confirmed || !quoteReady" class="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40" @click="submit">{{ busy ? 'Traitement…' : 'Proposer' }}</button>
          </div>
        </div>
      </template>
    </ModalComp>
  </section>
</template>
<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import ModalComp from '@/components/ui/ModalComp.vue';
import { useAuthStore } from '@/stores/auth';
import ReplacementComparison from './ReplacementComparison.vue';
import EditReplacementCandidateModal from './EditReplacementCandidateModal.vue';
import ReplacementProductPicker from './ReplacementProductPicker.vue';
import ReplacementProductPhoto from './ReplacementProductPhoto.vue';
import ReplacementReferenceSearch from './ReplacementReferenceSearch.vue';
import type { OrderProduct } from '@/types';
import { cancelReplacement, fetchOrderReplacements, fetchReplacementProducts, previewReplacement, proposeReplacement,
  replacementLineProducts, replacementMoney, replacementProductTotal, replacementStatus } from '@/api/remplacements';
import type { ReplacementAppliedProduct, ReplacementInput, ReplacementLine, ReplacementProduct, ReplacementProposal, ReplacementSelection } from '@/api/remplacements';

const props = defineProps<{ orderId: number; products: OrderProduct[]; canPropose: boolean }>();
const auth = useAuthStore();
const canManageLinks = computed(() => auth.isImpersonating);
const canEditRecommendations = computed(() => props.canPropose && canManageLinks.value);
const replacementStatusClasses: Record<ReplacementProposal['statut'], string> = {
  pending: 'bg-indigo-100 text-indigo-800 ring-indigo-200',
  accepted: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  rejected: 'bg-red-100 text-red-800 ring-red-200',
  expired: 'bg-amber-100 text-amber-900 ring-amber-200',
  cancelled: 'bg-gray-100 text-gray-700 ring-gray-200',
  confirmer: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
};
const emit = defineEmits<{ changed: []; settled: [ids: number[]]; replacementConfirmation: [value: boolean] }>();
const proposals = ref<ReplacementProposal[]>([]);
const now = ref(Date.now());
const products = ref<ReplacementProduct[]>([]);
const candidatesByLine = ref<Record<number, number[]>>({});
const appliedSearch = ref('');
let searchVersion = 0;
let searchTimer: ReturnType<typeof window.setTimeout> | null = null;
let quoteTimer: ReturnType<typeof window.setTimeout> | null = null;
let quoteVersion = 0;
const searchTargetLineId = ref(0);
const selections = ref<ReplacementSelection[]>([]);
const selectedLineIds = ref<number[]>([]);
const activeOriginIds = computed(() => selectedLineIds.value.filter(originEnabled));
const removedOriginIds = computed(() => selectedLineIds.value.filter(id => !originEnabled(id)));
const visibleOriginIds = computed(() => activeOriginIds.value.filter(id => id === searchTargetLineId.value));
const visible = ref(false);
const step = ref<1 | 2>(1);
const stepsHeader = ref<HTMLOListElement | null>(null);
const search = ref('');
const searching = ref(false);
const busy = ref(false);
const quoting = ref(false);
const quoteError = ref('');
const originConfirmations = ref<Record<number, { input: string; quote: string }>>({});
const preview = ref<ReplacementLine[] | null>(null);
const quoteHash = ref('');
const fixedDiscount = ref(0);
const modalError = ref('');
const loadError = ref('');
const loadInfo = ref('');
const expandedProposalId = ref<string | null>(null);
const candidateEditor = ref<InstanceType<typeof EditReplacementCandidateModal> | null>(null);
const sellerCharge = computed(() => (preview.value || []).reduce((sum, line) => sum + (line.charge_vendeur_max_ttc ?? line.charge_vendeur_ttc), 0));
const searchTarget = computed(() => selections.value.find(s => s.id_ligne_origine === searchTargetLineId.value));
const referenceSuggestions = computed(() => searchTarget.value ? availableProducts(searchTargetLineId.value) : []);
const original = (id: number) => props.products.find(p => p.id === id);
const ruptureProductIds = computed(() => new Set(props.products.map(p => Number(p.productId)).filter(id => Number.isSafeInteger(id) && id > 0)));
const product = (selection: ReplacementSelection) => products.value.find(p => p.id_produit === selection.id_produit_remplacement && !ruptureProductIds.value.has(p.id_produit));
function selectedProductTotal(selection: ReplacementSelection) {
  const candidate = product(selection);
  return candidate ? replacementProductTotal(candidate, selection) : { amount: null, description: 'Produit indisponible' };
}
const quotedLines = computed(() => new Map((preview.value || []).map(line => [line.id_ligne_origine, line])));
const quotedLine = (id: number) => quotedLines.value.get(id);
const quotedProducts = computed(() => new Map((preview.value || []).flatMap(line =>
  replacementLineProducts(line).map(candidate => [`${line.id_ligne_origine}:${candidate.id_produit}`, candidate] as const))));
const quotedProduct = (selection: ReplacementSelection) => quotedProducts.value.get(`${selection.id_ligne_origine}:${selection.id_produit_remplacement}`);
const readySelections = computed(() => selections.value.filter(selectionReady));
const incompleteOrigins = computed(() => selectedLineIds.value.filter(id => originEnabled(id) && (!selectedFor(id).length || selectedFor(id).some(s => !selectionReady(s)))));
const missingCandidates = computed(() => selectedLineIds.value.filter(id => originEnabled(id) && !selectedFor(id).length));
const candidatesChosen = computed(() => selections.value.length > 0 && missingCandidates.value.length === 0 && selections.value.every(s => Boolean(product(s))));
const stepIncompleteOrigins = computed(() => step.value === 1 ? missingCandidates.value : incompleteOrigins.value);
const complete = computed(() => selections.value.length > 0 && readySelections.value.length === selections.value.length);
const quoteReady = computed(() => complete.value && Boolean(quoteHash.value) && preview.value?.length === new Set(selections.value.map(s => s.id_ligne_origine)).size);
const confirmedOriginCount = computed(() => activeOriginIds.value.filter(originConfirmed).length);
const confirmed = computed(() => activeOriginIds.value.length > 0 && confirmedOriginCount.value === activeOriginIds.value.length);
const quoteKey = computed(() => JSON.stringify({ order: props.orderId, lines: selections.value.map(s => ({ ...s, price: product(s)?.prix_unitaire_ht, pack: product(s)?.pack, minimum: product(s)?.qte_mini, mode: product(s)?.vendu_par })) }));
const date = (value: string | number) => new Date(value).toLocaleString('fr-FR');
const input = (): ReplacementInput => ({ id_commande: props.orderId, lignes: selections.value, stock_confirme: confirmed.value, quote_hash: quoteHash.value });
const errorMessage = (error: unknown) => error instanceof Error ? error.message : 'Une erreur est survenue';
function responseDeadline(proposal: ReplacementProposal) {
  return new Date(proposal.date_expiration).getTime();
}
function remainingSeconds(proposal: ReplacementProposal) {
  return Math.max(0, Math.ceil((responseDeadline(proposal) - now.value) / 1000));
}
function countdown(proposal: ReplacementProposal) {
  const seconds = remainingSeconds(proposal);
  return [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60]
    .map(value => String(value).padStart(2, '0')).join(':');
}
function displayStatus(proposal: ReplacementProposal) {
  if (proposal.statut !== 'pending' || remainingSeconds(proposal) > 0) return proposal.statut;
  return proposal.lignes.some(line => line.statut === 'accepted' || line.accepted === 1) ? 'accepted' : 'expired';
}
function hasExpiredLines(proposal: ReplacementProposal) {
  return proposal.lignes.some(line => line.statut === 'expired') ||
    (proposal.statut === 'pending' && remainingSeconds(proposal) === 0);
}
function quantityErrors(selection: ReplacementSelection): string[] {
  if (ruptureProductIds.value.has(selection.id_produit_remplacement)) return ['Ce produit est déjà en rupture dans cette commande. Choisissez un autre candidat.'];
  const p = product(selection);
  if (!p) return ['Choisissez un produit de remplacement disponible.'];
  if (!Number.isSafeInteger(selection.quantite_pack) || selection.quantite_pack < 1 || selection.quantite_pack > 10000) return ['Quantité de paquets : saisissez un nombre entier entre 1 et 10 000.'];
  const q = selection.quantites;
  if (q.length !== 12) return ['Quantités incomplètes : sélectionnez à nouveau ce produit.'];
  const errors = q.flatMap((n, i) => {
    const label = p.tailles[i] || `Taille ${i + 1}`;
    if (!Number.isSafeInteger(n) || n < 0 || n > 10000) return [`${label} : saisissez un nombre entier entre 0 et 10 000.`];
    if (p.vendu_par === 'tailles') return n && !p.tailles[i] ? [`${label} : taille non disponible pour ce produit.`] : [];
    const maximum = p.pack[i] * selection.quantite_pack;
    return n <= maximum ? [] : [`${label} : ${n} pièce(s) saisie(s), maximum ${maximum} pour ${selection.quantite_pack} paquet(s).`];
  });
  if (errors.length) return errors;
  const count = q.reduce((sum, n) => sum + n, 0);
  if (count === 0) return ['Sélectionnez au moins une pièce pour ce produit.'];
  if (count > 10000) return ['Quantité totale : 10 000 pièces maximum par produit.'];
  if (p.vendu_par === 'tailles' && !(count >= p.qte_mini)) return [`Quantité totale : minimum ${p.qte_mini} pièce(s) pour ce produit (${count} saisie(s)).`];
  return [];
}
function selectionReady(selection: ReplacementSelection) { return quantityErrors(selection).length === 0; }
function candidateInputSignature(selection: ReplacementSelection) {
  const candidate = product(selection);
  return JSON.stringify({ selection, price: candidate?.prix_unitaire_ht, pack: candidate?.pack,
    minimum: candidate?.qte_mini, mode: candidate?.vendu_par });
}
function candidateQuoteSignature(selection: ReplacementSelection): string | null {
  const candidate = quotedProduct(selection);
  const origin = quotedLine(selection.id_ligne_origine)?.origine;
  if (!candidate || !origin || !selectionReady(selection)) return null;
  return JSON.stringify({ ht: candidate.montant_ht, ttc: candidate.montant_ttc,
    charge: candidate.charge_vendeur_ttc ?? 0, refund: candidate.a_rembourser_ttc ?? 0,
    originHT: origin.montant_ht, originTTC: origin.montant_ttc, discount: fixedDiscount.value });
}
function originInputSignature(lineId: number) {
  return JSON.stringify(selectedFor(lineId).map(candidateInputSignature));
}
function originQuoteSignature(lineId: number): string | null {
  const quotes = selectedFor(lineId).map(candidateQuoteSignature);
  return quotes.length && quotes.every(quote => quote !== null) ? JSON.stringify(quotes) : null;
}
function originConfirmed(lineId: number) {
  const saved = originConfirmations.value[lineId];
  return Boolean(saved && saved.input === originInputSignature(lineId) && saved.quote === originQuoteSignature(lineId));
}
function confirmOrigin(lineId: number, event: Event) {
  const quote = originQuoteSignature(lineId);
  if (busy.value || quoting.value || !originEnabled(lineId) || quote === null) return;
  if ((event.target as HTMLInputElement).checked)
    originConfirmations.value[lineId] = { input: originInputSignature(lineId), quote };
  else delete originConfirmations.value[lineId];
}
function pruneOriginConfirmations(checkQuote = false) {
  for (const [key, saved] of Object.entries(originConfirmations.value)) {
    const id = Number(key);
    if (!originEnabled(id) || saved.input !== originInputSignature(id) ||
      (checkQuote && saved.quote !== originQuoteSignature(id))) delete originConfirmations.value[id];
  }
}
function stopQuote() {
  if (quoteTimer != null) window.clearTimeout(quoteTimer);
  quoteTimer = null;
  quoteVersion++;
  quoting.value = false;
}
function invalidate() {
  stopQuote();
  pruneOriginConfirmations();
  preview.value = null; quoteHash.value = ''; fixedDiscount.value = 0; quoteError.value = '';
  if (visible.value && step.value === 2 && readySelections.value.length) {
    quoting.value = true;
    quoteTimer = window.setTimeout(() => { void refreshQuote(); }, 300);
  }
}
function availableProducts(lineId: number) {
  const ids = new Set(candidatesByLine.value[lineId] || []);
  return products.value.filter(p => ids.has(p.id_produit) && p.id_produit !== original(lineId)?.productId && !ruptureProductIds.value.has(p.id_produit));
}
function selectedFor(lineId: number) { return selections.value.filter(s => s.id_ligne_origine === lineId && s.id_produit_remplacement > 0); }
async function focusStepHeader() {
  await nextTick();
  stepsHeader.value?.scrollIntoView({ block: 'start' });
  stepsHeader.value?.focus({ preventScroll: true });
}
function nextStep() {
  if (busy.value || searching.value || !candidatesChosen.value) return;
  step.value = 2;
  modalError.value = '';
  invalidate();
  void focusStepHeader();
}
function previousStep() {
  if (busy.value) return;
  originConfirmations.value = {};
  step.value = 1;
  modalError.value = '';
  invalidate();
  void focusStepHeader();
}
async function focusOrigin(lineId: number) {
  if (step.value === 2 && !selectedFor(lineId).length) previousStep();
  selectOrigin(lineId);
  await nextTick();
  const section = document.getElementById(`replacement-origin-${lineId}`);
  section?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  section?.focus({ preventScroll: true });
}
function selectOrigin(lineId: number) {
  if (busy.value || !originEnabled(lineId)) return;
  searchTargetLineId.value = lineId;
}
function originEnabled(lineId: number) { return selections.value.some(s => s.id_ligne_origine === lineId); }
function toggleOrigin(lineId: number) {
  if (busy.value) return;
  if (originEnabled(lineId)) selections.value = selections.value.filter(s => s.id_ligne_origine !== lineId);
  else selections.value.push(emptySelection(lineId));
  if (!originEnabled(searchTargetLineId.value)) searchTargetLineId.value = activeOriginIds.value[0] || 0;
  modalError.value = '';
  invalidate();
}
function emptySelection(lineId: number): ReplacementSelection { return { id_ligne_origine: lineId, id_produit_remplacement: 0, quantites: Array(12).fill(0), quantite_pack: 1 }; }
function removeProduct(selection: ReplacementSelection) {
  modalError.value = '';
  selections.value = selections.value.filter(s => s !== selection);
  if (!selectedFor(selection.id_ligne_origine).length) selections.value.push(emptySelection(selection.id_ligne_origine));
  invalidate();
}
function toggleProduct(lineId: number, productId: number, allowRemoval = true) {
  const current = selectedFor(lineId).find(s => s.id_produit_remplacement === productId);
  if (current) { if (allowRemoval) removeProduct(current); return; }
  if (ruptureProductIds.value.has(productId)) { modalError.value = 'Ce produit est déjà en rupture dans cette commande. Choisissez un autre candidat.'; return; }
  if (selectedFor(lineId).length >= 10) { modalError.value = '10 articles maximum par ligne.'; return; }
  let selection = selections.value.find(s => s.id_ligne_origine === lineId && !s.id_produit_remplacement);
  if (!selection) {
    selections.value.push(emptySelection(lineId));
    selection = selections.value[selections.value.length - 1];
  }
  modalError.value = '';
  choose(selection, productId);
}
function originalAmount(id: number) {
  const p = original(id);
  const ht = p?.priceLineHT ?? p?.price;
  if (typeof ht !== 'number' || !Number.isFinite(ht)) return null;
  return `${replacementMoney(ht)} HT${p?.priceLineTTC != null ? ` · ${replacementMoney(p.priceLineTTC)} TTC` : ''}`;
}
function originalQuantity(id: number) {
  const p = original(id);
  const quantity = p?.quantites ? Object.values(p.quantites).reduce((sum, q) => sum + (Number(q) || 0), 0) : 0;
  return quantity || p?.quantite_total || p?.details || 0;
}
function originalSizes(id: number) {
  const p = original(id);
  return Object.entries(p?.quantites || {}).filter(([, quantity]) => Number(quantity) > 0).map(([key, quantity]) => {
    const label = p?.declinaisons?.[`d${key.slice(1)}_FR` as keyof NonNullable<OrderProduct['declinaisons']>];
    return `${label || 'Assortiment'} × ${quantity}`;
  }).join(' · ');
}
function sizeInputs(selection: ReplacementSelection) {
  const p = product(selection);
  return p ? p.tailles.map((label, index) => ({ label: label || 'Assortiment', index }))
    .filter(size => p.vendu_par === 'tailles' ? Boolean(p.tailles[size.index]) : p.pack[size.index] > 0) : [];
}
function updateSizeQuantity(selection: ReplacementSelection, index: number, event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.value === '' && !input.validity.badInput) input.value = '0';
  selection.quantites[index] = input.value === '' ? NaN : Number(input.value);
  invalidate();
}
function updatePacks(selection: ReplacementSelection) {
  const p = product(selection);
  if (p) selection.quantites = p.pack.map(q => q * selection.quantite_pack);
  invalidate();
}
function choose(selection: ReplacementSelection, productId: number) {
  if (selection.id_produit_remplacement === productId) return;
  selection.id_produit_remplacement = productId;
  selection.quantite_pack = 1;
  selection.quantites = Array(12).fill(0);
  const p = product(selection);
  if (p?.vendu_par !== 'tailles') updatePacks(selection);
  invalidate();
}
function chooseReference(productId: number) {
  if (!searchTarget.value) return;
  toggleProduct(searchTargetLineId.value, productId, false);
  search.value = products.value.find(p => p.id_produit === productId)?.reference || search.value;
}
function clearSearchTimer() {
  if (searchTimer != null) window.clearTimeout(searchTimer);
  searchTimer = null;
}
function protectedLineIds(rows: ReplacementProposal[]) {
  return rows.flatMap(proposal => proposal.lignes.filter(line => {
    const status = line.statut || (line.accepted ? 'accepted' : line.rejected ? 'rejected' : proposal.statut);
    return ['accepted', 'expired', 'pending'].includes(status);
  }).map(line => line.id_ligne_origine));
}
function onDetailsToggle(id: string, event: Event) {
  if (!(event.target as HTMLDetailsElement).open && expandedProposalId.value === id) expandedProposalId.value = null;
}
function openCandidateEditor(proposal: ReplacementProposal, line: ReplacementLine, product: ReplacementAppliedProduct, action: 'quantity' | 'remove' | 'product') {
  if (!canEditRecommendations.value) return;
  void candidateEditor.value?.open(proposal, line, product, action);
}
async function onCandidateSaved() {
  loadInfo.value = 'Recommandation mise à jour.';
  await load(); emit('changed');
}
async function load() {
  const id = props.orderId;
  try {
    const result = await fetchOrderReplacements(id, canManageLinks.value);
    if (id !== props.orderId) return;
    const changed = proposals.value.length > 0 && (proposals.value.length !== result.length || proposals.value.some(old => {
      const current = result.find(proposal => proposal.id_jet === old.id_jet);
      return !current || current.statut !== old.statut || current.confirmation_remplacements !== old.confirmation_remplacements ||
        JSON.stringify(current.lignes) !== JSON.stringify(old.lignes);
    }));
    proposals.value = result;
    loadError.value = '';
    emit('settled', protectedLineIds(result));
    emit('replacementConfirmation', result.some(proposal => proposal.confirmation_remplacements === true));
    if (changed) emit('changed');
  } catch (error) { loadError.value = errorMessage(error); emit('replacementConfirmation', false); }
}
async function searchProducts() {
  clearSearchTimer();
  const version = ++searchVersion;
  const orderId = props.orderId;
  const term = search.value.trim();
  searching.value = true;
  modalError.value = '';
  try {
    const requests = new Map<string, ReturnType<typeof fetchReplacementProducts>>();
    const results = await Promise.all(selectedLineIds.value.map(async lineId => {
      const category = original(lineId)?.id_categorie;
      const key = term ? 'reference' : category ? `category:${category}` : `line:${lineId}`;
      let request = requests.get(key);
      if (!request) {
        request = fetchReplacementProducts(orderId, term, lineId, canManageLinks.value);
        requests.set(key, request);
      }
      return { lineId, rows: await request };
    }));
    if (version !== searchVersion || orderId !== props.orderId || !visible.value) return;
    const selectedIds = new Set(selections.value.map(s => s.id_produit_remplacement));
    const merged = new Map(products.value.filter(p => selectedIds.has(p.id_produit)).map(p => [p.id_produit, p]));
    const byLine: Record<number, number[]> = {};
    for (const result of results) {
      byLine[result.lineId] = result.rows.map(p => p.id_produit);
      result.rows.forEach(p => merged.set(p.id_produit, p));
    }
    products.value = [...merged.values()];
    candidatesByLine.value = byLine;
    appliedSearch.value = term;
  } catch (error) { if (version === searchVersion) modalError.value = errorMessage(error); }
  finally { if (version === searchVersion) searching.value = false; }
}
async function open() {
  if (!props.canPropose) return;
  loadInfo.value = '';
  modalError.value = '';
  await load();
  if (!props.canPropose) return;
  const alreadyReplaced = new Set(protectedLineIds(proposals.value));
  const eligible = props.products.map(p => p.id).filter(id => !alreadyReplaced.has(id));
  if (!eligible.length) { loadError.value = 'Tous les articles sont déjà proposés, remplacés ou hors délai. Ajoutez une nouvelle ligne en rupture pour proposer un autre remplacement.'; return; }
  selectedLineIds.value = [...new Set(eligible)];
  selections.value = selectedLineIds.value.map(emptySelection);
  searchTargetLineId.value = eligible[0];
  candidatesByLine.value = {};
  search.value = '';
  appliedSearch.value = '';
  preview.value = null;
  originConfirmations.value = {};
  step.value = 1;
  visible.value = true;
  await searchProducts();
}
async function refreshQuote() {
  stopQuote();
  if (!props.canPropose || !visible.value || step.value !== 2 || !readySelections.value.length) return;
  const version = quoteVersion;
  const orderId = props.orderId;
  const lines = readySelections.value.map(s => ({ ...s, quantites: [...s.quantites] }));
  quoting.value = true;
  quoteError.value = '';
  try {
    const result = await previewReplacement({ id_commande: orderId, lignes: lines, stock_confirme: false }, canManageLinks.value);
    if (version !== quoteVersion || orderId !== props.orderId || !visible.value) return;
    preview.value = result.lignes;
    quoteHash.value = result.quote_hash;
    fixedDiscount.value = result.remise_fixe_commande_ht || 0;
  }
  catch (error) { if (version === quoteVersion && visible.value) quoteError.value = errorMessage(error); }
  finally { if (version === quoteVersion) quoting.value = false; }
}
async function submit() {
  if (!props.canPropose || step.value !== 2 || busy.value || quoting.value || !confirmed.value || !quoteReady.value) return;
  const orderId = props.orderId;
  busy.value = true;
  modalError.value = '';
  try {
    const result = await proposeReplacement(input());
    if (orderId !== props.orderId) return;
    loadInfo.value = result.lien_conserve ? 'Articles ajoutés à la proposition.' : '';
    visible.value = false;
    await load();
    emit('changed');
  } catch (error) { modalError.value = errorMessage(error); invalidate(); }
  finally { busy.value = false; }
}
async function cancel(id: string) {
  if (busy.value) return;
  busy.value = true;
  try { await cancelReplacement(id, props.orderId); loadInfo.value = ''; await load(); emit('changed'); }
  catch (error) { loadError.value = errorMessage(error); }
  finally { busy.value = false; }
}
watch(search, () => {
  clearSearchTimer();
  searchVersion++;
  candidatesByLine.value = {};
  if (!visible.value) return;
  searching.value = true;
  searchTimer = window.setTimeout(() => { void searchProducts(); }, search.value.trim() ? 300 : 0);
}, { flush: 'sync' });
watch(visible, value => {
  if (!value) { clearSearchTimer(); searchVersion++; searching.value = false; stopQuote(); }
});
watch(() => props.canPropose, allowed => { if (!allowed) visible.value = false; });
watch(() => props.orderId, () => { searchVersion++; searching.value = false; candidatesByLine.value = {}; visible.value = false; void load(); }, { immediate: true });
watch(quoteKey, invalidate, { flush: 'sync' });
watch([preview, fixedDiscount], () => { if (preview.value) pruneOriginConfirmations(true); }, { deep: true });
watch(() => proposals.value.filter(proposal => proposal.statut === 'pending' && remainingSeconds(proposal) === 0).map(proposal => proposal.id_jet).join(','), ids => {
  if (ids && !busy.value) void load();
});
const clock = window.setInterval(() => { now.value = Date.now(); }, 1000);
const timer = window.setInterval(() => { if (!visible.value && !busy.value) void load(); }, 30000);
onUnmounted(() => { window.clearInterval(clock); window.clearInterval(timer); clearSearchTimer(); searchVersion++; stopQuote(); });
defineExpose({ open });
</script>

<style scoped>
.replacement-proposal-modal :deep(.max-w-4xl) {
  max-width: 1120px;
}

.replacement-proposal-modal :deep(p) {
  margin-bottom: 0;
}

.replacement-origin-summary {
  padding: 12px 44px 12px 12px !important;
}

.replacement-sidebar {
  max-height: 56vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
}

.replacement-article--pending {
  --replacement-selection-color: #f59e0b;
  border-color: #f59e0b;
  background-color: #fffbeb;
}

.replacement-article--confirmed {
  --replacement-selection-color: #10b981;
  border-color: #10b981;
  background-color: #ecfdf5;
}

.replacement-article--active {
  box-shadow: 0 0 0 1px var(--replacement-selection-color);
}

.replacement-search :deep(input[role="combobox"]) {
  border-color: #e5e7eb;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  min-height: 38px;
}

.replacement-search :deep(input[role="combobox"]:focus) {
  border-color: #818cf8;
  outline: 2px solid #e0e7ff;
  outline-offset: 1px;
}

.replacement-picker :deep(legend) {
  color: #374151;
  font-weight: 600;
}

.replacement-picker :deep(.grid) {
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 150px), 1fr));
}

.replacement-picker :deep(button) {
  border-radius: 0.75rem;
  border-width: 1px;
}

.replacement-picker :deep(button > span:first-child) {
  height: 140px;
}

.replacement-picker :deep(button > span:last-child) {
  font-size: 0.75rem;
  padding: 0.75rem;
}

@media (max-width: 639px) {
  .replacement-sidebar {
    max-height: 36vh;
  }

  .replacement-proposal-modal :deep(.max-w-4xl) {
    padding: 1rem;
  }

  .replacement-actions {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  }

  .replacement-actions > button {
    min-width: 0;
    padding: 0.5rem 0.75rem !important;
  }
}
</style>
